import React, { useEffect, useState } from 'react'
import { View, Text, StyleSheet, Modal, ScrollView } from 'react-native'
import {
  Button,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  SIZE,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
  DatePicker,
} from '@dehaat/dds'
import FilterListItem from './FilterListItem'
import FilterHeader from './FilterHeader'
import CalendarIcon from '../../assests/icons/CalenderIcon'
import DateInputCard from '../reuseable/DateInputCard'
import FilterTab from './FilterTab'
import { trackEvent } from 'app/farmer360/utils/tracking'
import { CustomEventTypes } from '../../utils/events/CustomEventTypes'

type FilterModalState = Record<string, any>

interface Props {
  appliedFilterData: FilterModalState
  onApplyFilters: (data: FilterModalState) => void
  onClose: () => void
  filterList: any[]
}

export enum FORM_INPUT_TYPE {
  DATE_PICKER = 'DATE_PICKER',
  DROPDOWN = 'DROPDOWN',
  RADIO_BTN = 'RADIO_BTN',
  TEXT_BOX = 'TEXT_BOX',
  FILE_UPLOAD = 'FILE_UPLOAD',
  CHECK_BOX = 'CHECK_BOX',
  MULTI_SELECT_DROPDOWN = 'MULTI_SELECT_DROPDOWN',
  DATE_TIME_PICKER = 'DATE_TIME_PICKER',
  ARRAY = 'ARRAY',
  VALUE_UNIT_INPUT = 'VALUE_UNIT_INPUT',
  TEXT_BOX_NUMERIC = 'TEXT_BOX_NUMERIC',
  PIN_LOCATION = 'PIN_LOCATION',
}

const DATE_RANGE_LABELS = {
  FROM: 'fromDate',
  TO: 'toDate',
}

const FilterModal = ({
  filterList,
  onClose,
  appliedFilterData,
  onApplyFilters,
}: Props) => {
  const [tabIndex, selectedTabIndex] = useState(0)
  const [data, setData] = useState<any[]>([])
  const [appliedFilterState, setAppliedFilterState] =
    useState<FilterModalState>(appliedFilterData)
  const [isCalanderModalVisible, setIsCalanderModalVisible] = useState(false)
  const [activeDateField, setActiveDateField] = useState<string | null>(null)

  useEffect(() => {
    const incomingData = { ...appliedFilterData }

    if (incomingData.date_filter === 'custom') {
      incomingData.fromDate = ''
      incomingData.toDate = ''
    }

    setAppliedFilterState(incomingData)
  }, [appliedFilterData])

  useEffect(() => {
    if (filterList.length && tabIndex < filterList.length) {
      setData(filterList[tabIndex].filterData || [])
    }
  }, [tabIndex, filterList])

  const currentFilter = filterList[tabIndex] || {}
  const inputType = currentFilter.inputType
  const filterJsonKey = currentFilter.filterJsonKey
  const label = currentFilter.label || ''

  const checkIsChecked = (filterItemKey: string) => {
    if (inputType === FORM_INPUT_TYPE.DROPDOWN) {
      return appliedFilterState[filterJsonKey] === filterItemKey
    }
    if (inputType === FORM_INPUT_TYPE.MULTI_SELECT_DROPDOWN) {
      return (appliedFilterState[filterJsonKey] as string[])?.includes(
        filterItemKey,
      )
    }
    return false
  }

  const handleItemChange = (id: string) => {
    const appliedValue = appliedFilterState[filterJsonKey]
    let newValue: any = undefined

    if (inputType === FORM_INPUT_TYPE.DROPDOWN) {
      if (!appliedValue || appliedValue !== id) {
        newValue = id
      }
    } else if (inputType === FORM_INPUT_TYPE.MULTI_SELECT_DROPDOWN) {
      if (appliedValue) {
        newValue = [...(appliedValue as string[])]
        if (!(appliedValue as string[]).includes(id)) {
          newValue.push(id)
        } else {
          newValue = newValue.filter((appliedId: string) => appliedId !== id)
        }
      } else {
        newValue = [id]
      }
    }

    const newQuery = { ...appliedFilterState }

    if (newValue) {
      newQuery[filterJsonKey] = newValue
    } else {
      delete newQuery[filterJsonKey]
    }

    if (id === 'custom') {
      newQuery.fromDate = ''
      newQuery.toDate = ''
    } else {
      delete newQuery.fromDate
      delete newQuery.toDate
    }

    setAppliedFilterState(newQuery)
  }

  const getFilterCount = (categoryId: string) => {
    if (appliedFilterState[categoryId]) {
      const filterItems = appliedFilterState[categoryId]
      const itemCount = Array.isArray(filterItems)
        ? filterItems.length
        : [filterItems].filter(Boolean).length
      return itemCount
    }
    return 0
  }

  const handleApply = () => {
    const isCustom = appliedFilterState.date_filter === 'custom'
    const { fromDate, toDate } = appliedFilterState

    if (isCustom) {
      if (!fromDate || !toDate) {
        return
      }

      if (fromDate > toDate) {
        return
      }
    }
    trackEvent(CustomEventTypes.CropDocImagePastDiagnosisFilterApplied,{
      FilterType:appliedFilterState.date_filter
    })
    onApplyFilters(appliedFilterState)
  }

  if (!filterList?.length || tabIndex >= filterList.length) return null

  return (
    <Modal visible={true} onRequestClose={onClose}>
      <FilterHeader
        onClose={onClose}
        onClearAll={() => setAppliedFilterState({})}
      />
      <View style={[COMMON_STYLES.row, COMMON_STYLES.fullHeight]}>
        <View style={[COMMON_STYLES.verticalDivider, { width: 140 }]}>
          <ScrollView>
            {filterList.map((item, index) => (
              <FilterTab
                key={item.filterJsonKey}
                label={item.filterName}
                isHighlighted={tabIndex === index}
                filterCount={getFilterCount(item.filterJsonKey)}
                onClick={() => selectedTabIndex(index)}
              />
            ))}
          </ScrollView>
        </View>

        <View
          style={[COMMON_STYLES.flex1, COMMON_STYLES.p1, COMMON_STYLES.pb3]}
        >
          <Text
            style={[
              TEXT_STYLES.textNeutral90,
              VARIANTS_STYLES.MEDIUM,
              TYPOGRAPHY.TEXT1,
            ]}
          >
            {label}
          </Text>

          <ScrollView>
            <View style={COMMON_STYLES.pb5}>
              {(inputType === FORM_INPUT_TYPE.MULTI_SELECT_DROPDOWN ||
                inputType === FORM_INPUT_TYPE.DROPDOWN) &&
                data.map(({ id, value }) => (
                  <FilterListItem
                    key={`${filterJsonKey}_${id}`}
                    isSelected={checkIsChecked(id)}
                    onChange={() => {                 
                      trackEvent(CustomEventTypes.CropDocImagePastDiagnosisFilterClick)
                      handleItemChange(id)}}
                    filterId={id}
                    filterName={value}
                  />
                ))}

              {inputType === FORM_INPUT_TYPE.DROPDOWN &&
                appliedFilterState[filterJsonKey] === 'custom' && (
                  <View style={{ gap: 12 }}>
                    <DateInputCard
                      label="From Date"
                      value={appliedFilterState.fromDate}
                      onPress={() => {
                        setActiveDateField(DATE_RANGE_LABELS.FROM)
                        setIsCalanderModalVisible(true)
                      }}
                      icon={<CalendarIcon />}
                    />
                    <DateInputCard
                      label="To Date"
                      value={appliedFilterState.toDate}
                      onPress={() => {
                        setActiveDateField(DATE_RANGE_LABELS.TO)
                        setIsCalanderModalVisible(true)
                      }}
                      icon={<CalendarIcon />}
                    />
                  </View>
                )}
            </View>
          </ScrollView>
        </View>
      </View>

      <DatePicker
        visible={isCalanderModalVisible}
        onClose={() => {
          setIsCalanderModalVisible(false)
          setActiveDateField(null)
        }}
        onConfirm={date => {
          if (!date || typeof date !== 'string') {
            setIsCalanderModalVisible(false)
            setActiveDateField(null)
            return
          }

          if (activeDateField === DATE_RANGE_LABELS.FROM) {
            setAppliedFilterState(prev => ({
              ...prev,
              fromDate: date,
            }))
          } else if (activeDateField === DATE_RANGE_LABELS.TO) {
            setAppliedFilterState(prev => ({
              ...prev,
              toDate: date,
            }))
          }

          setIsCalanderModalVisible(false)
          setActiveDateField(null)
        }}
      />

      <View
        style={[
          COMMON_STYLES.absolute,
          COMMON_STYLES.bottom,
          COMMON_STYLES.row,
          COMMON_STYLES.justifyBetween,
          COMMON_STYLES.fullWidth,
          COMMON_STYLES.ph1,
          COMMON_STYLES['pv1/4'],
          {
            backgroundColor: COLORS.white,
            borderTopColor: COLORS.neutral10,
            borderTopWidth: 1,
          },
        ]}
      >
        <Button
          buttonStyle={styles.btnStyle}
          text={'Close'}
          onClick={onClose}
          type={BUTTON_TYPE.TERTIARY}
          size={SIZE.large}
        />
        <Button
          buttonStyle={
            appliedFilterState.date_filter === 'custom' &&
            (!appliedFilterState.fromDate || !appliedFilterState.toDate)
              ? styles.disabled
              : styles.btnStyle
          }
          text={'Apply'}
          onClick={handleApply}
          size={SIZE.large}
        />
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  btnStyle: {
    flex: 0.5,
    ...COMMON_STYLES.borderNone,
  },
  disabled: {
    backgroundColor: COLORS.neutral40,
    ...TEXT_STYLES.textWhite,
    borderColor: COLORS.neutral40,
    flex: 0.5,
  },
})

export default FilterModal
